import { PlateService } from './plateService';

export interface VoteResult {
  success: boolean;
  error?: string;
  newVoteCount?: {
    upvotes: number;
    downvotes: number;
    total: number;
  };
}

export class VoteService {
  private static readonly VOTE_STORAGE_KEY = 'rateMyPlate_votes';
  private static readonly IP_STORAGE_KEY = 'rateMyPlate_ip';

  /**
   * Gets a unique identifier for the current user/session
   */
  private static getVoterId(): string {
    // Try to get stored IP first
    let voterId = localStorage.getItem(this.IP_STORAGE_KEY);
    
    if (!voterId) {
      // Generate a unique ID for this session
      voterId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      localStorage.setItem(this.IP_STORAGE_KEY, voterId);
    }
    
    return voterId;
  }

  /**
   * Gets all votes made by the current user
   */
  private static getUserVotes(): Record<string, { voteType: 'upvote' | 'downvote'; date: string }> {
    try {
      const votes = localStorage.getItem(this.VOTE_STORAGE_KEY);
      if (!votes) return {};
      
      const parsedVotes = JSON.parse(votes);
      
      // Handle migration from old format (just vote type) to new format (with date)
      const migratedVotes: Record<string, { voteType: 'upvote' | 'downvote'; date: string }> = {};
      for (const [plateId, voteData] of Object.entries(parsedVotes)) {
        if (typeof voteData === 'string') {
          // Old format: just the vote type
          migratedVotes[plateId] = {
            voteType: voteData as 'upvote' | 'downvote',
            date: new Date().toISOString().split('T')[0] // Use today as fallback
          };
        } else {
          // New format: object with voteType and date
          migratedVotes[plateId] = voteData as { voteType: 'upvote' | 'downvote'; date: string };
        }
      }
      
      return migratedVotes;
    } catch (error) {
      console.error('Error parsing user votes:', error);
      return {};
    }
  }

  /**
   * Saves a vote to localStorage
   */
  private static saveUserVote(plateId: string, voteType: 'upvote' | 'downvote'): void {
    try {
      const votes = this.getUserVotes();
      votes[plateId] = {
        voteType,
        date: new Date().toISOString().split('T')[0] // Store as YYYY-MM-DD
      };
      localStorage.setItem(this.VOTE_STORAGE_KEY, JSON.stringify(votes));
    } catch (error) {
      console.error('Error saving user vote:', error);
    }
  }

  /**
   * Checks if the user has already voted on a specific plate
   */
  static hasUserVoted(plateId: string): boolean {
    const votes = this.getUserVotes();
    return plateId in votes;
  }

  /**
   * Gets the user's vote for a specific plate
   */
  static getUserVote(plateId: string): 'upvote' | 'downvote' | null {
    const votes = this.getUserVotes();
    return votes[plateId]?.voteType || null;
  }

  /**
   * Gets the user's vote data (including date) for a specific plate
   */
  static getUserVoteData(plateId: string): { voteType: 'upvote' | 'downvote'; date: string } | null {
    const votes = this.getUserVotes();
    return votes[plateId] || null;
  }

  /**
   * Submits a vote for a license plate
   */
  static async submitVote(
    plateId: string, 
    voteType: 'upvote' | 'downvote'
  ): Promise<VoteResult> {
    try {
      // Check if user already voted
      if (this.hasUserVoted(plateId)) {
        return {
          success: false,
          error: 'You have already voted on this plate'
        };
      }

      // Submit vote to backend
      const voterId = this.getVoterId();
      await PlateService.updateVotes(plateId, voteType, voterId);

      // Save vote locally
      this.saveUserVote(plateId, voteType);

      return {
        success: true
      };
    } catch (error) {
      console.error('Error submitting vote:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit vote'
      };
    }
  }

  /**
   * Changes a user's vote (upvote to downvote or vice versa)
   */
  static async changeVote(
    plateId: string, 
    newVoteType: 'upvote' | 'downvote'
  ): Promise<VoteResult> {
    try {
      const currentVote = this.getUserVote(plateId);
      
      if (!currentVote) {
        return {
          success: false,
          error: 'No existing vote found'
        };
      }

      if (currentVote === newVoteType) {
        return {
          success: false,
          error: 'You have already voted this way'
        };
      }

      // For now, we'll just update the local vote
      // In a real implementation, you'd need to handle vote changes on the backend
      this.saveUserVote(plateId, newVoteType);

      return {
        success: true
      };
    } catch (error) {
      console.error('Error changing vote:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to change vote'
      };
    }
  }

  /**
   * Removes a user's vote (for future use)
   */
  static async removeVote(plateId: string): Promise<VoteResult> {
    try {
      if (!this.hasUserVoted(plateId)) {
        return {
          success: false,
          error: 'No vote found to remove'
        };
      }

      // Remove from localStorage
      const votes = this.getUserVotes();
      delete votes[plateId];
      localStorage.setItem(this.VOTE_STORAGE_KEY, JSON.stringify(votes));

      return {
        success: true
      };
    } catch (error) {
      console.error('Error removing vote:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to remove vote'
      };
    }
  }

  /**
   * Gets vote statistics for a user
   */
  static getUserVoteStats(): {
    totalVotes: number;
    upvotes: number;
    downvotes: number;
  } {
    const votes = this.getUserVotes();
    const voteValues = Object.values(votes);
    
    return {
      totalVotes: voteValues.length,
      upvotes: voteValues.filter(v => v.voteType === 'upvote').length,
      downvotes: voteValues.filter(v => v.voteType === 'downvote').length
    };
  }

  /**
   * Clears all user votes (for testing or logout)
   */
  static clearUserVotes(): void {
    localStorage.removeItem(this.VOTE_STORAGE_KEY);
    localStorage.removeItem(this.IP_STORAGE_KEY);
  }
}
